import * as form from '../form';
import * as file from '../file';
import * as dialog from '../dialog';
import * as script from '../script';
import * as validator from './validator';
import {i18n} from './i18n';
import type {i18nKeys} from './i18n';
import {FacultyController} from './wizard/faculty';
import countries from './countries.json';
import {Template} from '../template';

interface Country {
  name: string;
  name_en: string;
}

function countryName(country: Country) {
  switch (document.documentElement.lang) {
    case 'ja':
      return country.name;
    case 'en':
      return country.name_en;
  }
  throw new Error;
}

class StudentStatusController extends form.Controller {
  readonly pattern =
    /^applicant\[answers_attributes\]\[\d+\]\[student_status_attributes\]\[regular_type\]$/;
  readonly listeners = [
    new form.Listener('change', this.onchange, this.pattern),
  ];
  constructor() {
    super();
    const none: HTMLInputElement | null =
      document.querySelector('#student-status-regular-type-none');
    if (none && none.form![none.name].value == '')
      this.update(none, true);
  }
  async onready(control: form.Control) {
    const radio = control as HTMLInputElement;
    if (radio.checked)
      this.update(radio, true);
  }
  private onchange(control: form.Control) {
    this.update(control as HTMLInputElement, false);
  }
  private update(radio: HTMLInputElement, ready: boolean) {
    const container = document.querySelector('#regular-input')!;
    const regular = parseInt(radio.value);
    const disabled = regular < 0;
    container.classList.toggle('standby', disabled);
    for (const c of Form.getControls(radio.form!)) {
      if (!container.contains(c))
        continue;
      c.tabIndex = disabled ? -1 : 0;
      if ('disabled' in c)
        c.disabled = disabled;
    }
    if (disabled)
      return;
    const select: HTMLSelectElement =
      document.querySelector('#student-status-student-type')!;
    switch (regular) {
      case 0:
        select.classList.remove('nonregular');
        select.classList.add('regular');
        break;
      case 1:
        select.classList.remove('regular');
        select.classList.add('nonregular');
        break;
    }
    if (!ready)
      select.selectedIndex = 0;
    const other: HTMLInputElement =
      document.querySelector('#student-status-student-type-other')!;
    other.disabled = false;
    const label =
      document.querySelector('#student-status-student-type-other-label')!;
    label.classList.remove('standby');
  }
}

class StudentStatusStudentTypeController extends form.Controller {
  readonly pattern =
    /^applicant\[answers_attributes\]\[\d+\]\[student_status_attributes\]\[student_type\]$/;
  readonly listeners = [
    new form.Listener('change', this.onchange, this.pattern),
  ];
  async onready(control: form.Control) {
    this.update(control as HTMLSelectElement);
  }
  private onchange(control: form.Control) {
    this.update(control as HTMLSelectElement);
  }
  private update(select: HTMLSelectElement) {
    const other: HTMLInputElement =
      document.querySelector('#student-status-student-type-other')!;
    const disabled = select.value != '';
    other.tabIndex = disabled ? -1 : 0;
    other.disabled = disabled;
    if (disabled) {
      select.required = true;
      other.value = '';
    }
    const label =
      document.querySelector('#student-status-student-type-other-label')!;
    label.classList.toggle('standby', disabled);
  }
}

class StudentStatusStudentTypeOtherController extends form.Controller {
  readonly pattern =
    /^applicant\[answers_attributes\]\[\d+\]\[student_status_attributes\]\[student_type_other\]$/;
  readonly listeners = [
    new form.Listener('change', this.onchange, this.pattern),
  ];
  async onready(control: form.Control) {
    this.update(control as HTMLInputElement);
  }
  private onchange(control: form.Control) {
    this.update(control as HTMLInputElement);
  }
  private update(input: HTMLInputElement) {
    const select: HTMLSelectElement =
      document.querySelector('#student-status-student-type')!;
    select.required = !input.value;
  }
}

class FileControlGroup {
  readonly file: HTMLInputElement = this.form[this.prefix];
  readonly download: HTMLInputElement = this.form[`${this.prefix}[download]`];
  readonly anchor = this.download.parentElement as HTMLAnchorElement;
  readonly filename: HTMLDivElement;
  constructor(private readonly form: HTMLFormElement,
              private readonly prefix: string) {
    const container =
      this.file.parentElement!.parentElement!.parentElement!.parentElement!;
    this.filename =
      container.querySelector('.upload-filename') as HTMLDivElement;
  }
  ready() {
    if (!this.anchor.getAttribute('href'))
      this.download.classList.add('hidden');
  }
  async change() {
    const file = this.file.files![0]!;
    if (!await this.validate(file))
      return;
    this.filename.textContent = file.name;
    this.anchor.href = URL.createObjectURL(file);
    this.download.classList.remove('hidden');
  }
  private validate(file: File) {
    if (!this.validateFileSize(file))
      return this.error('Too large file (MAX 10MB).');
    if (!this.validateFileType(file))
      return this.error('Unsupported file format.');
    return true;
  }
  private validateFileSize(file: File) {
    return file.size <= 10 * 1024 * 1024;
  }
  private validateFileType(file: File) {
    if (!this.file.accept)
      return true;
    const types = this.file.accept.split(',');
    return types.some(type => this.accept(type, file));
  }
  private accept(type: string, file: File) {
    return /^.+\/.+$/.test(type) && type == file.type ||
      type.startsWith('.') && file.name.endsWith(type);
  }
  private async error(message: string) {
    await dialog.alert(message);
    this.file.value = '';
    this.file.required = true;
    this.filename.textContent = 'No file';
    this.download.classList.remove('hidden');
    return false;
  }
}

class FileController extends form.Controller {
  readonly pattern = /^applicant\[answers_attributes\]\[\d+\]\[attachment\]/;
  readonly listeners = [
    new form.Listener('change', this.onchange, this.pattern),
  ];
  async onready(control: form.Control, matcher: RegExpMatchArray) {
    this.createControlGroup(control.form!, matcher[0]!).ready();
  }
  protected onchange(control: form.Control, matcher: RegExpMatchArray) {
    this.createControlGroup(control.form!, matcher[0]!).change();
  }
  protected createControlGroup(form: HTMLFormElement, prefix: string) {
    return new FileControlGroup(form, prefix);
  }
}

class ApplicationFeeController extends form.Controller {
  readonly pattern = /^application_fee_type$/;
  readonly listeners = [
    new form.Listener('change', this.onchange, this.pattern),
  ];
  async onready(control: form.Control) {
    this.update(control);
  }
  private onchange(control: form.Control) {
    this.update(control);
  }
  private update(control: form.Control) {
    const radios = control.form![control.name] as HTMLInputElement[];
    for (const radio of radios) {
      const div =
        document.getElementsByClassName(radio.value)[0] as HTMLDivElement;
      div.style.display = radio.checked ? '' : 'none';
      for (const control of Form.getControls(radio.form!))
        if (div.contains(control)) {
          control.tabIndex = radio.checked ? 0 : -1;
          if ('disabled' in control)
            control.disabled = radio.checked ? false : true;
        }
    }
  }
}

class RadioController extends form.Controller {
  readonly pattern =
    /^(applicant\[answers_attributes\]\[\d+\]\[radio_attributes\])\[code\]$/;
  readonly listeners = [
    new form.Listener('change', this.onchange, this.pattern),
  ];
  async onready(control: form.Control, matcher: RegExpMatchArray) {
    const radio = control as HTMLInputElement;
    if (radio.checked)
      this.update(control, matcher);
  }
  private onchange(control: form.Control, matcher: RegExpMatchArray) {
    this.update(control, matcher);
  }
  private update(control: form.Control, matcher: RegExpMatchArray) {
    const radio = control as HTMLInputElement;
    const other = this.other(radio.form!, matcher);
    if (!other)
      return;
    if (radio.value == '99') {
      other.readOnly = false;
      other.classList.remove('standby');
    } else {
      other.readOnly = true;
      other.classList.add('standby');
      other.value = '';
    }
  }
  private other(form: HTMLFormElement, matcher: RegExpMatchArray) {
    return form[`${matcher[1]}[other]`] as HTMLInputElement;
  }
}

class Form extends file.FetchForm {
  static setCustomValidity(control: form.Control, message: i18nKeys) {
    control.classList.add('invalid-input');
    control.setCustomValidity(i18n(message));
  }
  private readonly validators = [new validator.TextMonthValidator(this.form)];
  constructor(form: HTMLFormElement) {
    super(form, [new FacultyController,
                 new StudentStatusController,
                 new StudentStatusStudentTypeController,
                 new StudentStatusStudentTypeOtherController,
                 new ApplicationFeeController,
                 new RadioController,
                 new FileController,
                ]);
    FacultyController.onready();
  }
  override async validate() {
    return await super.validate() &&
      this.validators.every(validator => validator.checkValidity()) &&
      await WizardPage.page.checkValidity();
  }
  override onsubmit(event: SubmitEvent) {
    const button = event.submitter as HTMLButtonElement;
    if (button.name == 'validate') {
      form.Form.changed = true;
      return dialog.alert('エラーは見つかりませんでした');
    }
    WizardPage.page.onsubmit();
    return super.onsubmit(event);
  }
  protected async onresponse(response: Response, event: SubmitEvent) {
    const loading = Form.showLoadingModal();
    if (!response.ok)
      throw new Error;
    const button = event.submitter as HTMLButtonElement;
    if (button.name == 'save') {
      location.reload();
      return;
    }
    const object = await response.json();
    if (object.nextURL)
      location.href = object.nextURL;
    else if (object.aaoError) {
      loading.remove();
      document.documentElement.style.cursor = '';
      dialog.alert(Question.Aao.message(object.aaoError));
    }
  }
  protected override checkValidity() {
    return FacultyController.checkValidity();
  }
}

form.Form.register('wizard', Form);

class Script extends script.Script {
  static readonly pathname = /\/wizards\//;
  script() {
    document.addEventListener('click', event => {
      const target = event.target as HTMLElement;
      if (target.tagName.toLowerCase() == 'button' &&
          target.classList.contains('button-toggle-details'))
        this.toggleDetails(target as HTMLButtonElement);
    });
    const details = document.querySelectorAll('button.button-toggle-details');
    for (const button of Array.from(details))
      button.textContent = i18n('application_fee.see_details');
  }
  toggleDetails(button: HTMLButtonElement) {
    const section = button.parentElement!;
    section.classList.toggle('show-details');
    const wrapper = section.querySelector('.toggle-contents') as HTMLElement;
    wrapper.style.height = '0';
    button.textContent = i18n('application_fee.see_details');
    if (section.classList.contains('show-details')) {
      button.textContent = i18n('application_fee.hide_details');
      const clone = wrapper.cloneNode(true) as HTMLElement;
      clone.style.position = 'absolute';
      clone.style.visibility = 'hidden';
      clone.style.height = 'auto';
      wrapper.before(clone);
      const height = clone.offsetHeight;
      clone.remove();
      setTimeout(() => {
        wrapper.style.height = `${height}px`;
      });
    }
  }
}

script.Script.registry.push(Script);

class Question {
  readonly id: number;
  readonly description: string;
  readonly parentId: number;
  readonly parentCode: string[];
  readonly children: Question[] = [];
  readonly selected: string[];
  constructor(readonly page: WizardPage, data: any) {
    this.id = data.id;
    this.description = data['description'] ?? '';
    this.selected = data.selected || [];
    this.parentId = data.parent_question_id;
    this.parentCode = data.parent_question_code;
  }
  callbackDescription(element: HTMLElement) {
    if (element.hasChildNodes())
      return;
    const range = element.ownerDocument.createRange();
    range.selectNodeContents(element);
    range.insertNode(range.createContextualFragment(this.description));
  }
  created() {
    const parent =
      this.page.questions.find(question => question.id == this.parentId)!;
    if (!parent)
      return true;
    if (!parent.created())
      return false;
    return parent.selected.some(value => this.parentCode.includes(value));
  }
  async checkValidity() {
    return true;
  }
  onsubmit() {
  }
  onformdata(_data: FormData) {
  }
}

namespace Question {
  class AttachedOneUploader extends file.AttachmentUploader {
    protected createFileInputValidator(input: HTMLInputElement) {
      return new file.FileInputValidator(input, 25);
    }
  }

  class AttachedManyUploader extends AttachedOneUploader {
    protected override createClientAttachment(name: string, file: File) {
      return new ClientAttachment(this, `${name}[]`, file);
    }
  }

  class ClientAttachment extends file.ClientAttachment<AttachedManyUploader> {
  }

  export class Radio extends Question {
    readonly onchange: EventListener = event => {
      const input = event.target as HTMLInputElement;
      this.selected.splice(0);
      this.selected.push(input.value);
      this.page.template.update();
    };
    checked(value: string) {
      return this.selected.includes(value);
    }
  }

  export class Checkbox extends Question {
    private readonly min: number;
    private readonly max: number;
    readonly onchange: EventListener = event => {
      const input = event.target as HTMLInputElement;
      if (input.checked) {
        if (this.max && this.max <= this.selected.length)
          input.checked = false;
        else
          this.selected.push(input.value);
      } else
        this.selected.splice(this.selected.indexOf(input.value), 1);
      this.page.template.update();
    };
    constructor(page: WizardPage, data: any) {
      super(page, data);
      this.min = data.min;
      this.max = data.max;
    }
    checked(value: string) {
      return this.selected.includes(value);
    }
    override async checkValidity() {
      if (!this.min || this.min <= this.selected.length)
        return true;
      const checkboxes =
        this.page.template.references(this, 'checkbox') as HTMLInputElement[];
      for (const checkbox of checkboxes) {
        checkbox.classList.add('invalid-input');
        checkbox.setCustomValidity(i18n('checkbox.min', {min: this.min}));
      }
      return false;
    }
  }

  export class Select extends Question {
    readonly onchange: EventListener = event => {
      const input = event.target as HTMLSelectElement;
      this.selected.splice(0);
      this.selected.push(input.value);
      this.page.template.update();
    };
  }

  export class FileQuestion extends Question {
    readonly uploader;
    readonly required: boolean;
    readonly multiple: boolean;
    readonly answerId: number;
    readonly requests;
    impossibleReason: string;
    constructor(page: WizardPage, data: any) {
      super(page, data);
      this.uploader =
        new FileQuestion.AttachmentUploader(page, this, data['uploads']);
      this.required = data['required'];
      this.multiple = data['multiple'];
      this.answerId = data['answer_id'];
      this.requests = data['upload_requests'].map((data: any) => {
        return new FileQuestion.UploadRequest(data);
      });
      this.impossibleReason = data['impossible_reason'] ?? '';
    }
    onclickuploadrequestform() {
      const dialog = document.querySelector('#upload-request')!.
        cloneNode(true) as HTMLDialogElement;
      const form = dialog.querySelector('form')!;
      new FileQuestion.UploadRequestForm(form, dialog, this);
      document.body.append(dialog);
      dialog.showModal();
    }
    onchangeimpossiblereason(event: Event) {
      const textarea = event.target as HTMLTextAreaElement;
      this.impossibleReason = textarea.value;
      this.page.template.update(this);
    }
    override onsubmit() {
      this.uploader.onsubmit();
    }
    override async checkValidity() {
      if (!this.required || this.uploader.count > 0 ||
          this.requests.length > 0 || this.impossibleReason.length > 0)
        return true;
      const template = this.page.template;
      const upload =
        template.reference(this.uploader, 'upload') as HTMLInputElement;
      if (upload) {
        upload.classList.add('invalid-input');
        upload.setCustomValidity(i18n('required'));
      }
      const request =
        template.reference(this, 'request-button') as HTMLButtonElement;
      if (request) {
        request.classList.add('invalid-input');
        request.setCustomValidity(i18n('required'));
      }
      const reason =
        template.reference(this, 'impossible-reason') as HTMLTextAreaElement;
      if (reason) {
        reason.classList.add('invalid-input');
        reason.setCustomValidity(i18n('required'));
      }
      return false;
    }
    override onformdata(data: FormData) {
      this.uploader.onformdata(data);
    }
  }

  export namespace FileQuestion {
    export class AttachmentUploader extends file.AttachmentUploader {
      constructor(
        page: WizardPage,
        private readonly question: FileQuestion,
        attachments: any[]) {
        super(page, attachments);
      }
      get multiple() {
        return this.question.multiple;
      }
      showUploadButton() {
        return this.multiple ? this.count < 5 : this.count == 0;
      }
      protected createFileInputValidator(input: HTMLInputElement) {
        return new file.FileInputValidator(input, 25, this.count);
      }
      protected override createClientAttachment(name: string, file: File) {
        return new ClientAttachment(this, `${name}[]`, file);
      }
      protected override createServerAttachment(data: any) {
        return new ServerAttachment(this, data);
      }
    }

    class ClientAttachment extends file.ClientAttachment<AttachmentUploader> {
    }

    class ServerAttachment extends file.ServerAttachment<AttachmentUploader> {
      override onclickdelete() {
        super.onclickdelete();
      }
      override onsubmit() {
        if (this.has)
          return;
        const index = this.uploader.attachments.indexOf(this);
        const template = this.uploader.page.template;
        const id = template.reference(this, 'id') as HTMLInputElement;
        id.name = id.name.replace(/\[0\]\[id\]$/, `[${index}][id]`);
        const destroy =
          template.reference(this, 'destroy') as HTMLInputElement;
        destroy.name = destroy.name.replace(/\[0\]\[_destroy\]$/,
                                            `[${index}][_destroy]`);
      }
    }

    export class UploadRequest {
      readonly name;
      readonly email;
      readonly href;
      readonly files: {
        readonly uploadedAt: string;
        readonly href: string;
      }[];
      constructor(data: any) {
        this.name = data['name'];
        this.email = data['email'];
        this.href = data['href'];
        this.files = data['files'].map((data: any) => {
          return {
            uploadedAt: data['uploaded_at'],
            href: data['href'],
          }
        });
      }
    }

    export class UploadRequestForm extends form.Form {
      constructor(form: HTMLFormElement,
                  private readonly dialog: HTMLDialogElement,
                  private readonly question: FileQuestion) {
        super(form, []);
        dialog.addEventListener('close', () => {
          dialog.remove();
        });
        const cancel =
          form.querySelector('button[name="cancel"]') as HTMLButtonElement;
        cancel.addEventListener('click', () => {
          dialog.close();
        });
        document.body.append(dialog);
      }
      override async onsubmit() {
        const data = new FormData(this.form);
        data.append('upload_request[answer_id]', String(this.question.answerId));
        const response = await form.Form.loading(() => {
          return fetch(this.form.action, {
            method: this.form.method, body: data,
          });
        });
        if (!response.ok)
          throw new Error;
        const name = this.form['upload_request[name]'].value;
        const email = this.form['upload_request[email]'].value;
        const request = new UploadRequest({name: name, email: email, files: []});
        this.question.requests.push(request);
        this.question.page.template.update(this.question);
        this.dialog.close();
      }
    }
  }

  export class Aao extends Question {
    aaoEmail = '';
    aaoPassword = '';
    aaoId: number;
    readonly hasAaoAccount: boolean;
    constructor(page: WizardPage, data: any) {
      super(page,data);
      this.aaoEmail = data["aao"]["email"];
      this.aaoId = data["aao"]["id"];
      this.hasAaoAccount = Boolean(this.aaoId);
    }
    onchangeemail(event: Event) {
      const input = event.target as HTMLInputElement;
      this.aaoEmail = input.value;
    }
    onchangepassword(event: Event) {
      const input = event.target as HTMLInputElement;
      this.aaoPassword = input.value;
    }
    onchangeid(event: Event) {
      const input = event.target as HTMLInputElement;
      this.aaoId = input.valueAsNumber;
    }
    static message(status: number) {
      switch (status) {
        case 200:
          return i18n('aao_ok');
        case 403: case 404:
          return i18n('aao_not_found');
        case 409:
          return i18n('aao_conflict');
        case 500:
          return 'AAO SERVER ERROR';
        default:
          return 'UNKNOWN ERROR';
      }
    }
    override async checkValidity() {
      if (this.hasAaoAccount)
        return true;
      if (this.aaoEmail && this.aaoPassword && this.aaoId)
        return true;
      if (!(this.aaoEmail || this.aaoPassword || this.aaoId))
        return true;
      const references = {
        email: this.aaoEmail,
        password: this.aaoPassword,
        id: this.aaoId,
      };
      for (const [key, value] of Object.entries(references)) {
        if (value)
          continue;
        const element =
          WizardPage.page.template.reference(this, key) as HTMLInputElement;
        element.classList.add('invalid-input');
        element.setCustomValidity(i18n('required'));
      }
      return false;
    }
  }

  export class PersonalInformation extends Question {
    nationalityType = '';
    countries: PersonalInformation.Country[];
    readonly photo;
    readonly passport;
    readonly aaoId?: number;
    constructor(page: WizardPage, data: any) {
      super(page, data);
      this.nationalityType = data['nationality_type'] ?? '';
      this.countries = data['countries'].map((country: any) => {
        return new PersonalInformation.Country(this, country);
      });
      if (this.countries.length == 0)
        this.countries = [new PersonalInformation.Country(this)];
      this.photo = new PersonalInformation.PhotoUploader(
        page, data['photo'] ? [data['photo']] : []);
      this.passport = new AttachedOneUploader(
        page, data['passport'] ? [data['passport']] : []);
      this.aaoId = data['aao_id'];
    }
    onchangenamekana(event: Event) {
      const input = event.target as HTMLInputElement;
      input.value = input.value.trim().normalize('NFKC').
        replace(/ /g, '\u3000').
        replace(/[\u3041-\u3096]/g, c => {
          const code =
            c.charCodeAt(0) - '\u3041'.charCodeAt(0) + '\u30a1'.charCodeAt(0);
          return String.fromCharCode(code);
        }).replace(/[^\u3000\u30a0-\u30ff]*/g, '');
    }
    onchangenameinenglish(event: Event) {
      const input = event.target as HTMLInputElement;
      input.value =
        input.value.trim().normalize('NFKC').replace(/[^ -~]*/g, '');
    }
    async onchangenationalitytype(event: Event) {
      const input = event.target as HTMLInputElement;
      this.nationalityType = input.value;
      this.page.template.update();
    }
    onclickaddcountry() {
      this.countries.push(new PersonalInformation.Country(this));
      this.page.template.update(this);
    }
    get addableCountry() {
      return this.countries.length <= 1;
    }
    hasAaoId() {
      return Boolean(this.aaoId);
    }
    hasAaoIdAndNationality() {
      return this.hasAaoId() && this.countries.filter(c => c.id).length > 0;
    }
    override async checkValidity() {
      if (this.nationalityType == 'foreign') {
        const valids = this.countries.map(country => country.checkValidity())
        return valids.every(valid => valid);
      } else
        return true;
    }
    override onformdata(data: FormData) {
      this.photo.onformdata(data);
      this.passport.onformdata(data);
    }
  }

  export namespace PersonalInformation {
    export class Country {
      id = '';
      text = '';
      constructor(private readonly question: PersonalInformation, data?: any) {
        if (data) {
          this.id = data['id'];
          this.text = data['text'];
        }
      }
      onchangetext(event: Event) {
        const input = event.target as HTMLInputElement;
        const country = countries.find(
          country => countryName(country) == input.value);
        this.id = country ? country.code : '';
        this.text = input.value;
        this.question.page.template.update(this);
      }
      get disabled() {
        return this.question.nationalityType == 'japan';
      }
      hasAaoId() {
        return this.question.hasAaoId();
      }
      onclickremove() {
        this.question.countries.splice(this.index, 1);
        this.question.page.template.update(this.question);
      }
      get removable() {
        return this.index > 0;
      }
      private get index() {
        return this.question.countries.indexOf(this);
      }
      checkValidity() {
        return this.checkValidityCountryId();
      }
      private checkValidityCountryId() {
        if (this.id)
          return true;
        const template = this.question.page.template;
        const input = template.reference(this, 'text') as HTMLInputElement;
        input.classList.add('invalid-input');
        input.classList.add(i18n('invalid'));
        return false;
      }
    }

    export class PhotoUploader extends AttachedOneUploader {
      private name!: string;
      private file!: File;
      constructor(page: WizardPage, data: any) {
        super(page, data);
      }
      override async onchangefile(event: Event) {
        const input = event.target as HTMLInputElement;
        try {
          const validator = this.createFileInputValidator(input);
          if (await validator.valid())
            for (const file of input.files!) {
              const dialog = this.queryDialogElement();
              const img = dialog.querySelector('img')!;
              img.src = URL.createObjectURL(file);
              dialog.showModal();
              this.name = input.name;
              this.file = file;
            }
        } finally {
          input.value = '';
        }
      }
      onclickok() {
        this.attachments.push(
          this.createClientAttachment(this.name, this.file));
        this.page.template.update(this);
        this.queryDialogElement().close();
      }
      onclickcancel() {
        this.queryDialogElement().close();
      }
      private queryDialogElement() {
        const template = this.page.template;
        return template.reference(this, 'preview') as HTMLDialogElement;
      }
    }
  }

  export class ContactInformation extends Question {
    readonly currentAddress;
    readonly previousAddress;
    constructor(page: WizardPage, data: any) {
      super(page, data);
      this.currentAddress =
        new ContactInformation.Address(this, data['current_address']);
      this.previousAddress =
        new ContactInformation.Address(this, data['previous_address']);
    }
    override async checkValidity() {
      const valids =
        [this.currentAddress, this.previousAddress].map(
          address => address.checkValidity());
      return valids.every(valid => valid);
    }
    override onformdata(data: FormData) {
      this.currentAddress.onformdata(data);
    }
  }

  export namespace ContactInformation {
    export class Address {
      nationType = '';
      countryId = '';
      countryText = '';
      postalCode = '';
      address = '';
      prefectureId = '';
      address1 = '';
      address2 = '';
      address3 = '';
      phoneNumber = '';
      mobilePhoneNumber = '';
      faxPhoneNumber = '';
      email = '';
      readonly visa;
      constructor(private readonly question: ContactInformation, data: any) {
        this.nationType = data['nation_type'] ?? 'japan';
        this.countryId = data['country_id'] ?? '';
        this.countryText = data['country_text'] ?? '';
        this.postalCode = data['postal_code'] ?? '';
        this.address = data['address'] ?? '';
        this.prefectureId = data['prefecture_id'] ?? '';
        this.address1 = data['address1'] ?? '';
        this.address2 = data['address2'] ?? '';
        this.address3 = data['address3'] ?? '';
        this.phoneNumber = data['phone_number'] ?? '';
        this.mobilePhoneNumber = data['mobile_phone_number'] ?? '';
        this.faxPhoneNumber = data['fax_phone_number'] ?? '';
        this.email = data['email'] ?? '';
        this.visa = new AttachedOneUploader(
          question.page, data['visa'] ? [data['visa']] : []);
      }
      onchangenationtype(event: Event) {
        const input = event.target as HTMLInputElement;
        this.nationType = input.value;
        this.question.page.template.update(this.question);
      }
      onchangecountrytext(event: Event) {
        const input = event.target as HTMLInputElement;
        this.countryText = input.value;
        const country = countries.find(c => countryName(c) == input.value);
        if (country)
          this.countryId = country.code;
        else
          this.countryId = '';
        this.question.page.template.update(this);
      }
      onchangepostalcode(event: Event) {
        const input = event.target as HTMLInputElement;
        input.value = input.value.normalize('NFKC');
        this.postalCode = input.value;
      }
      onchangeaddress(event: Event) {
        const input = event.target as HTMLInputElement;
        this.address = input.value;
      }
      onchangeprefectureid(event: Event) {
        const input = event.target as HTMLInputElement;
        this.prefectureId = input.value;
      }
      onchangeaddress1(event: Event) {
        const input = event.target as HTMLInputElement;
        input.value = this.toFullWidth(input.value);
        this.address1 = input.value;
        this.question.page.template.update(this);
      }
      onchangeaddress2(event: Event) {
        const input = event.target as HTMLInputElement;
        input.value = this.toFullWidth(input.value);
        this.address2 = input.value;
        this.question.page.template.update(this);
      }
      private toFullWidth(text: string) {
        text = text.normalize('NFKC');
        text = text.replace(/[!-~]/g, s => {
          return String.fromCharCode(s.charCodeAt(0) - '!'.charCodeAt(0) +
                                     '\uff01'.charCodeAt(0)); // '！'
        });
        return text;
      }
      onchangeaddress3(event: Event) {
        const input = event.target as HTMLInputElement;
        this.address3 = input.value;
      }
      onchangephonenumber(event: Event) {
        const input = event.target as HTMLInputElement;
        input.value = input.value.normalize('NFKC')
        this.phoneNumber = input.value;
      }
      get phoneNumberPrefix() {
        const match = this.phoneNumber.match(/^(.*?)-(.*)$/);
        return match ? match[1] : '';
      }
      get phoneNumberLocal() {
        const match = this.phoneNumber.match(/^(.*?)-(.*)$/);
        return match ? match[2] : '';
      }
      onchangephonenumberprefix(event: Event) {
        const input = event.target as HTMLInputElement;
        input.value = input.value.normalize('NFKC')
        this.phoneNumber = `${input.value}-${this.phoneNumberLocal}`;
      }
      onchangephonenumberlocal(event: Event) {
        const input = event.target as HTMLInputElement;
        input.value = input.value.normalize('NFKC')
        this.phoneNumber = `${this.phoneNumberPrefix}-${input.value}`;
      }
      onchangemobilephonenumber(event: Event) {
        const input = event.target as HTMLInputElement;
        input.value = input.value.normalize('NFKC')
        this.mobilePhoneNumber = input.value;
      }
      get mobilePhoneNumberPrefix() {
        const match = this.mobilePhoneNumber.match(/^(.*?)-(.*)$/);
        return match ? match[1] : '';
      }
      get mobilePhoneNumberLocal() {
        const match = this.mobilePhoneNumber.match(/^(.*?)-(.*)$/);
        return match ? match[2] : '';
      }
      onchangemobilephonenumberprefix(event: Event) {
        const input = event.target as HTMLInputElement;
        input.value = input.value.normalize('NFKC')
        this.mobilePhoneNumber =
          `${input.value}-${this.mobilePhoneNumberLocal}`;
      }
      onchangemobilephonenumberlocal(event: Event) {
        const input = event.target as HTMLInputElement;
        input.value = input.value.normalize('NFKC')
        this.mobilePhoneNumber =
          `${this.mobilePhoneNumberPrefix}-${input.value}`;
      }
      onchangefaxphonenumber(event: Event) {
        const input = event.target as HTMLInputElement;
        input.value = input.value.normalize('NFKC')
        this.faxPhoneNumber = input.value;
      }
      get faxPhoneNumberPrefix() {
        const match = this.faxPhoneNumber.match(/^(.*?)-(.*)$/);
        return match ? match[1] : '';
      }
      get faxPhoneNumberLocal() {
        const match = this.faxPhoneNumber.match(/^(.*?)-(.*)$/);
        return match ? match[2] : '';
      }
      onchangefaxphonenumberprefix(event: Event) {
        const input = event.target as HTMLInputElement;
        input.value = input.value.normalize('NFKC')
        this.faxPhoneNumber = `${input.value}-${this.faxPhoneNumberLocal}`;
      }
      onchangefaxphonenumberlocal(event: Event) {
        const input = event.target as HTMLInputElement;
        input.value = input.value.normalize('NFKC')
        this.faxPhoneNumber = `${this.faxPhoneNumberPrefix}-${input.value}`;
      }
      showResidentStatus() {
        for (const question of this.question.page.questions)
          if (question instanceof PersonalInformation)
            return question.nationalityType == 'foreign';
        return false;
      }
      async onclickpostalcode() {
        if (!/^\d{7}$/.test(this.postalCode)) {
          await dialog.alert('7桁の郵便番号を入力してください');
          return;
        }
        const url =
          new URL(location.href.replace(/\/wizards\/.*$/, '/postal_code'));
        url.searchParams.append('postal_code', this.postalCode);
        try {
          const data = await Form.loading(async () => {
            const response = await fetch(url.href);
            return response.json();
          });
          if (data['prefecture_id']){
            this.prefectureId = data['prefecture_id'];
            this.address1 = data['address1'];
            this.address2 = data['address2'];
            this.question.page.template.update(this);
          } else {
            await dialog.alert('郵便番号が見つかりませんでした');
          }
        } catch {
          location.reload();
        }
      }
      onclickcopyaddress() {
        this.copyFrom(this.question.currentAddress);
        this.question.page.template.update(this);
      }
      copyFrom(address: Address) {
        this.nationType = address.nationType;
        this.countryId = address.countryId;
        this.countryText = address.countryText;
        this.postalCode = address.postalCode;
        this.address = address.address;
        this.prefectureId = address.prefectureId;
        this.address1 = address.address1;
        this.address2 = address.address2;
        this.address3 = address.address3;
        this.phoneNumber = address.phoneNumber;
        this.mobilePhoneNumber = address.mobilePhoneNumber;
        this.faxPhoneNumber = address.faxPhoneNumber;
        this.email = address.email;
      }
      checkValidity() {
        return [
          this.checkValidityCountryId(),
          this.checkValidityHomePhoneOrMobilePhone(),
          this.checkValidityFaxPhoneNumber(),
        ].every(valid => valid);
      }
      private checkValidityCountryId() {
        if (this.countryId)
          return true;
        const template = this.question.page.template;
        const input = template.reference(this, 'country') as HTMLInputElement;
        if (!input)
          return true;
        input.classList.add('invalid-input');
        input.classList.add(i18n('invalid'));
        return false;
      }
      private checkValidityHomePhoneOrMobilePhone() {
        const template = this.question.page.template;
        const home =
          template.reference(this, 'homePhoneNumber') as HTMLInputElement;
        const mobile =
          template.reference(this, 'mobilePhoneNumber') as HTMLInputElement;
        if (!home || !mobile)
          return true;
        if (this.phoneNumber || this.mobilePhoneNumber)
          return true;
        home.classList.add('invalid-input');
        home.setCustomValidity(
          '自宅電話番号，携帯電話番号のどちらか一方は必ず入力してください');
        mobile.classList.add('invalid-input');
        mobile.setCustomValidity(
          '自宅電話番号，携帯電話番号のどちらか一方は必ず入力してください');
        return false;
      }
      private checkValidityFaxPhoneNumber() {
        const template = this.question.page.template;
        const prefix =
          template.reference(this, 'faxPhoneNumberPrefix') as HTMLInputElement;
        const local =
          template.reference(this, 'faxPhoneNumberLocal') as HTMLInputElement;
        if (!prefix || !local)
          return true;
        if (!this.faxPhoneNumberPrefix && this.faxPhoneNumberLocal) {
          prefix.classList.add('invalid-input');
          prefix.setCustomValidity(i18n('required'));
          return false;
        }
        if (this.faxPhoneNumberPrefix && !this.faxPhoneNumberLocal) {
          local.classList.add('invalid-input');
          local.setCustomValidity(i18n('required'));
          return false;
        }
        return true;
      }
      onformdata(data: FormData) {
        this.visa.onformdata(data);
      }
    }
  }

  abstract class QuestionWithBackgrounds<T extends QuestionWithBackgrounds.Background> extends Question {
    abstract type: string;
    readonly backgrounds: T[];
    readonly required: boolean;
    constructor(page: WizardPage, data: any) {
      super(page, data);
      this.required = data['required'] ?? false;
      this.backgrounds = data['backgrounds'].map((background: any) => {
        return this.createBackground(background);
      });
      if (this.required && this.backgrounds.length == 0)
        this.backgrounds.push(this.createBackground());
    }
    abstract createBackground(data?: any): T;
    onclickadd() {
      this.backgrounds.push(this.createBackground());
      WizardPage.page.template.update(this);
    }
    override onsubmit() {
      for (const background of this.backgrounds)
        background.onsubmit();
    }
    override async checkValidity() {
      const valids = await Promise.all(this.backgrounds.map(
        background => background.checkValidity()));
      return valids.every(valid => valid);
    }
  }

  namespace QuestionWithBackgrounds {
    export function formatMonths(months: number) {
      if (months == 0)
        return '';
      const m = months % 12;
      const y = (months - m) / 12;
      const my = [m == 0 ? '' : m == 1 ? '1 month' : `${m} months`,
                  y == 0 ? '' : y == 1 ? '1 year' : `${y} years`];
      return my.filter(text => text).join(' ');
    }

    export class Background {
      id = 0;
      _destroy: '' | '1' = '';
      name = '';
      periodFrom = '';
      periodTo = '';
      constructor(readonly question: QuestionWithBackgrounds<Background>, data?: any) {
        if (data) {
          this.id = data['id'];
          this.name = data['name'];
          this.periodFrom = data['period_from'].replace(/-01$/, '');
          this.periodTo = data['period_to'].replace(/-01$/, '');
        }
      }
      onclickremove() {
        this._destroy = '1';
        WizardPage.page.template.update(this.question);
      }
      get removable() {
        return this.index > 0 || !this.question.required;
      }
      get index() {
        return this.question.backgrounds.indexOf(this);
      }
      onchangename(event: Event) {
        const input = event.target as HTMLInputElement;
        this.name = input.value;
      }
      onchangeperiodfrom(event: Event) {
        const input = event.target as HTMLInputElement;
        this.periodFrom = input.value;
        WizardPage.page.template.update(this);
      }
      onchangeperiodto(event: Event) {
        const input = event.target as HTMLInputElement;
        this.periodTo = input.value;
        WizardPage.page.template.update(this);
      }
      inputmonthcallback(element: HTMLInputElement) {
        if (element.parentElement)
          return;
        element.placeholder = 'YYYY-MM';
        element.pattern = '\\d{4}-\\d{2}';
      }
      get period() {
        return formatMonths(this.calculatePeriodMonths());
      }
      private calculatePeriodMonths() {
        const from = this.toDate(this.periodFrom);
        const to = this.toDate(this.periodTo);
        return from && to && from.getTime() <= to.getTime() ?
          this.toMonths(to) - this.toMonths(from) + 1 : 0;
      }
      private toMonths(date: Date) {
        return date.getFullYear() * 12 + date.getMonth();
      }
      private toDate(text: string) {
        const time = Date.parse(text);
        if (!time)
          return;
        const date = new Date(time);
        if (!date.toISOString().startsWith(text))
          return;
        return date;
      }
      onsubmit() {
        const template = WizardPage.page.template;
        const form =
          template.reference(WizardPage.page, 'form') as HTMLFormElement;
        const background =
          template.reference(this, 'background') as HTMLDivElement;
        for (const control of Form.getControls(form)) {
          if (!background.contains(control))
            continue;
          const split = control.name.split('][');
          if (/^\d+$/.test(String(split[1])))
            split[1] = String(this.index);
          control.name = split.join('][');
        }
      }
      async checkValidity() {
        if (this._destroy)
          return true;
        return this.checkValidityPeriod();
      }
      private checkValidityPeriod() {
        const from = this.toDate(this.periodFrom);
        const to = this.toDate(this.periodTo);
        if (from && to && from.getTime() <= to.getTime())
          return true;
        if (!from)
          this.setInvalid('periodFrom', i18n('invalid'));
        if (!to)
          this.setInvalid('periodTo', i18n('invalid'));
        if (from && to && from.getTime() > to.getTime()) {
          this.setInvalid('periodFrom', i18n('background.from_month'));
          this.setInvalid('periodTo', i18n('background.to_month'));
        }
        return false;
      }
      protected setInvalid(key: string, message: string) {
        const template = WizardPage.page.template;
        const input = template.reference(this, key) as HTMLInputElement;
        input.classList.add('invalid-input');
        input.setCustomValidity(message);
      }
    }
  }

  abstract class Education<T extends Education.Background> extends QuestionWithBackgrounds<T> {
    readonly useRemarks: boolean;
    readonly calculator = Education;
    constructor(page: WizardPage, data: any) {
      super(page, data);
      this.useRemarks = data['use_remarks'];
      if (this.backgrounds.length == 0 && this.required)
        this.backgrounds.push(this.createBackground());
    }
  }

  namespace Education {
    export function calculateTotalPeriod() {
      const months = flatMapEducationBackgrounds().reduce(
        (sum, background) => sum + background.calculatePeriodMonths(), 0);
      return QuestionWithBackgrounds.formatMonths(months);
    }

    export function calculateTotalMinimumPeriod() {
      const years = flatMapEducationBackgrounds().reduce(
        (sum, background) => sum + (background.minimumPeriod || 0), 0);
      return QuestionWithBackgrounds.formatMonths(years * 12);
    }

    function flatMapEducationBackgrounds() {
      return WizardPage.page.questions.filter(question => {
        return question instanceof OtherEducation == false;
      }).flatMap(question => {
        return question instanceof Education ? question.backgrounds : [];
      }).filter(background => !background._destroy);
    }

    export class Background extends QuestionWithBackgrounds.Background {
      override readonly question;
      minimumPeriod?: number;
      remarks = '';
      readonly aaoEducationTypeId?: number;
      constructor(question: Education<Education.Background>, data?: any) {
        super(question, data);
        this.question = question;
        if (data) {
          this.minimumPeriod = data['minimum_period'];
          this.remarks = data['remarks'];
          this.aaoEducationTypeId = data['aao_education_type_id'];
        }
      }
      fromAao() {
        return !(this.question.page.isProxyLogin ||
                 this.aaoEducationTypeId == undefined);
      }
      override onchangeperiodfrom(event: Event) {
        super.onchangeperiodfrom(event);
        WizardPage.page.template.update(Education);
      }
      override onchangeperiodto(event: Event) {
        super.onchangeperiodto(event);
        WizardPage.page.template.update(Education);
      }
      onchangeminimumperiod(event: Event) {
        const input = event.target as HTMLInputElement;
        this.minimumPeriod = input.valueAsNumber;
        WizardPage.page.template.update(this);
        WizardPage.page.template.update(Education);
      }
      onchangeremarks(event: Event) {
        const input = event.target as HTMLInputElement;
        this.remarks = input.value;
      }
    }
  }

  export class UndergraduateEducation extends Education<UndergraduateEducation.Background> {
    type = 'undergraduate_education';
    diplomaFilesConfig;
    transcriptFilesConfig;
    otherFilesConfig;
    constructor(page: WizardPage, data: any) {
      super(page, data);
      this.diplomaFilesConfig =
        new UndergraduateEducation.FilesConfig(data['diploma_files']);
      this.transcriptFilesConfig =
        new UndergraduateEducation.FilesConfig(data['transcript_files']);
      this.otherFilesConfig =
        new UndergraduateEducation.FilesConfig(data['other_files']);
      for (const background of this.backgrounds) {
        background.diplomaFiles.config = this.diplomaFilesConfig;
        background.transcriptFiles.config = this.transcriptFilesConfig;
        background.otherFiles.config = this.otherFilesConfig;
      }
    }
    createBackground(data?: any) {
      const background = new UndergraduateEducation.Background(this, data);
      background.diplomaFiles.config = this.diplomaFilesConfig;
      background.transcriptFiles.config = this.transcriptFilesConfig;
      background.otherFiles.config = this.otherFilesConfig;
      return background;
    }
    override onformdata(data: FormData) {
      for (const background of this.backgrounds)
        background.onformdata(data);
    }
  }

  export namespace UndergraduateEducation {
    export class Background extends Education.Background {
      department = '';
      status = '';
      readonly diplomaFiles;
      readonly transcriptFiles;
      readonly otherFiles;
      override question: UndergraduateEducation;
      constructor(question: UndergraduateEducation, data?: any) {
        super(question, data);
        this.question = question;
        if (data) {
          this.department = data['department'];
          this.periodFrom = data['period_from'];
          this.periodTo = data['period_to'];
          this.status = data['status'];
        }
        this.diplomaFiles =
          new AttachmentUploader(this, data?.['diploma_files']);
        this.transcriptFiles =
          new AttachmentUploader(this, data?.['transcript_files']);
        this.otherFiles =
          new AttachmentUploader(this, data?.['other_files']);
      }
      onchangedepartment(event: Event) {
        const input = event.target as HTMLInputElement;
        this.department = input.value;
      }
      onchangestatus(event: Event) {
        const select = event.target as HTMLSelectElement;
        this.status = select.value;
      }
      inputdatecallback(element: HTMLInputElement) {
        if (element.parentElement)
          return;
        element.placeholder = 'YYYY-MM-DD';
        element.pattern = '\\d{4}-\\d{2}-\\d{2}';
      }
      override onsubmit() {
        super.onsubmit();
        if (this._destroy)
          return;
        this.diplomaFiles.onsubmit();
        this.transcriptFiles.onsubmit();
        this.otherFiles.onsubmit();
      }
      onformdata(data: FormData) {
        if (this._destroy)
          return;
        this.diplomaFiles.onformdata(data);
        this.transcriptFiles.onformdata(data);
        this.otherFiles.onformdata(data);
      }
    }

    export class FilesConfig {
      readonly use: boolean;
      readonly required: boolean;
      readonly descriptionJa = '';
      readonly descriptionEn = '';
      constructor(data: any) {
        this.use = data['use'];
        this.required = data['required'];
        this.descriptionJa = data['description_ja'];
        this.descriptionEn = data['description_en'];
      }
    }

    class AttachmentUploader extends file.AttachmentUploader {
      config!: FilesConfig;
      constructor(readonly background: UndergraduateEducation.Background,
                  data?: any) {
        super(background.question.page, data);
      }
      protected createFileInputValidator(input: HTMLInputElement) {
        return new file.FileInputValidator(input, 25, this.count);
      }
      protected override createClientAttachment(
        name: string, file: File): ClientAttachment {
        return new ClientAttachment(this, name, file);
      }
      protected override createServerAttachment(data: any): ServerAttachment {
        return new ServerAttachment(this, data);
      }
      get invalid() {
        return this.count == 0 && this.config.required;
      }
    }

    class ClientAttachment extends file.ClientAttachment<AttachmentUploader> {
      override onformdata(data: FormData) {
        if (!this.has)
          return;
        const split = this.name.split('][');
        split[1] = String(this.uploader.background.index);
        data.append(split.join(']['), this.file, this.filename);
      }
    }

    class ServerAttachment extends file.ServerAttachment<AttachmentUploader> {
      override onsubmit() {
        const template = this.uploader.page.template;
        const form =
          template.reference(WizardPage.page, 'form') as HTMLFormElement;
        const attachment =
          template.reference(this, 'attachment') as HTMLDivElement;
        if (!attachment)
          return;
        for (const control of Form.getControls(form)) {
          if (!attachment.contains(control))
            continue;
          const split = control.name.split('][');
          const index = this.uploader.attachments.indexOf(this);
          if (split[3] && /^\d+/.test(split[3]))
            split[3] = String(index);
          control.name = split.join('][');
        }
      }
    }
  }

  export class GraduateEducation extends UndergraduateEducation {
    override type = 'graduate_education';
    override createBackground(data?: any) {
      const background = new GraduateEducation.Background(this, data);
      background.diplomaFiles.config = this.diplomaFilesConfig;
      background.transcriptFiles.config = this.transcriptFilesConfig;
      background.otherFiles.config = this.otherFilesConfig;
      return background;
    }
  }

  export namespace GraduateEducation {
    export class Background extends UndergraduateEducation.Background {
      studentType = '';
      constructor(question: GraduateEducation, data?: any) {
        super(question, data);
        if (data) {
          this.studentType = data['student_type'];
        }
      }
      onchangestudenttype(event: Event) {
        const select = event.target as HTMLSelectElement;
        this.studentType = select.value;
      }
    }
  }

  export class HigherEducation extends Education<HigherEducation.Background> {
    type = 'higher_education';
    createBackground(data?: any) {
      return new HigherEducation.Background(this, data);
    }
  }

  export namespace HigherEducation {
    export class Background extends Education.Background {
      countryId = '';
      countryText = '';
      constructor(question: HigherEducation, data?: any) {
        super(question, data);
        if (data) {
          this.countryId = data['country_id'] ?? '';
          this.countryText = data['country_text'] ?? '';
        }
      }
      onchangecountrytext(event: Event) {
        const input = event.target as HTMLInputElement;
        this.countryText = input.value;
        const country = countries.find(c => countryName(c) == input.value);
        this.countryId = country?.code || '';
        WizardPage.page.template.update(this);
      }
      override async checkValidity() {
        const valids = await Promise.all([super.checkValidity(),
                                          this.checkValidityCountryId()]);
        return valids.every(valid => valid);
      }
      private checkValidityCountryId() {
        if (!this.countryText || this.countryId)
          return true;
        this.setInvalid('countryText', i18n('invalid'));
        return false;
      }
    }
  }

  export class SecondaryEducation extends Education<SecondaryEducation.Background> {
    type = 'secondary_education';
    createBackground(data?: any): SecondaryEducation.Background {
      return new SecondaryEducation.Background(this, data);
    }
  }

  export namespace SecondaryEducation {
    export class Background extends Education.Background {
    }
  }

  export class ElementaryEducation extends Education<ElementaryEducation.Background> {
    type = 'elementary_education';
    createBackground(data?: any): ElementaryEducation.Background {
      return new ElementaryEducation.Background(this, data);
    }
  }

  export namespace ElementaryEducation {
    export class Background extends Education.Background {
    }
  }

  export class OtherEducation extends Education<OtherEducation.Background> {
    type = 'other_education';
    createBackground(data?: any): OtherEducation.Background {
      return new OtherEducation.Background(this, data);
    }
  }

  export namespace OtherEducation {
    export class Background extends Education.Background {
      readonly aaoEducationTypeName = '';
      constructor(question: OtherEducation, data?: any) {
        super(question, data);
        if (data) {
          this.aaoEducationTypeName = data['aao_education_type_name'] ?? '';
        }
      }
    }
  }

  export class EmploymentRecord extends QuestionWithBackgrounds<EmploymentRecord.Background> {
    type = 'employment_record';
    createBackground(data?: any) {
      return new EmploymentRecord.Background(this, data);
    }
  }

  export namespace EmploymentRecord {
    export class Background extends QuestionWithBackgrounds.Background {
      status = '';
      constructor(question: EmploymentRecord, data?: any) {
        super(question, data);
        if (data)
          this.status = data['status'];
      }
      onchangestatus(event: Event) {
        const input = event.target as HTMLInputElement;
        this.status = input.value;
        WizardPage.page.template.update(this);
      }
    }
  }

  export class Internship extends QuestionWithBackgrounds<QuestionWithBackgrounds.Background> {
    type = 'internship';
    createBackground(data?: any): QuestionWithBackgrounds.Background {
      return new QuestionWithBackgrounds.Background(this, data);
    }
  }
  
  export class LanguageProficiency extends Question {
    readonly checkboxes: number[] = [];
    readonly useScore: boolean;
    readonly min?: number;
    readonly max?: number;
    readonly entries: LanguageProficiency.Entry[];
    constructor(page: WizardPage, data: any) {
      super(page, data);
      this.checkboxes = data['checkboxes'];
      this.useScore = data['use_score'];
      this.min = data['min'];
      this.max = data['max'];
      this.entries = data['entries'].map((entry: any) => {
        return new LanguageProficiency.Entry(this, entry);
      });
      for (const answer of data['answer_entries']) {
        const entry = this.entry(answer['name']);
        if (!entry)
          continue;
        entry.checked = true;
        entry.examinationDate = answer['examination_date'];
        entry.score = answer['score'];
      }
      for (const attachment of data['attachments']) {
        const entry = this.entry(attachment['title']);
        entry.attachment.id = attachment['id'];
        entry.attachment.filename = attachment['filename'];
        entry.attachment.href = attachment['href'];
        entry.attachment.has = true;
      }
    }
    onchangecheckbox(event: Event) {
      const checkbox = event.target as HTMLInputElement;
      const value = parseInt(checkbox.value);
      if (checkbox.checked)
        this.checkboxes.push(value);
      else
        this.checkboxes.splice(this.checkboxes.indexOf(value), 1);
      this.page.template.update(this);
    }
    scoreEnabled() {
      return this.useScore || this.checkboxes.length == 0;
    }
    checked(value: number) {
      return this.checkboxes.includes(value);
    }
    entry(name: string) {
      return this.entries.find(entry => entry.name == name)!;
    }
    update(object: {}) {
      this.page.template.update(object);
    }
    override async checkValidity() {
      if (this.min && this.min > this.entries.filter(
        entry => entry.checked).length) {
        const template = this.page.template;
        for (const entry of this.entries) {
          const checkbox =
            template.reference(entry, 'checkbox') as HTMLInputElement;
          checkbox.classList.add('invalid-input');
          checkbox.setCustomValidity(i18n('checkbox.min', {min: this.min}));
        }
        return false;
      }
      if (!this.scoreEnabled())
        return true;
      const valids = this.entries.map(entry => entry.checkValidity());
      return valids.every(valid => valid);
    }
    override onsubmit() {
      for (const entry of this.entries)
        entry.onsubmit();
    }
    override onformdata(data: FormData) {
      for (const entry of this.entries)
        entry.onformdata(data);
    }
  }

  export namespace LanguageProficiency {
    export class Entry {
      readonly name: string;
      readonly required: boolean;
      readonly expired: string;
      readonly upload: boolean;
      readonly scoreInput: boolean;
      readonly scoreRequired: boolean;
      checked: boolean = false;
      examinationDate = '';
      score?: number;
      readonly attachment: Attachment;
      readonly examinationDateComment: string;
      constructor(readonly question: LanguageProficiency, data: any) {
        this.name = data['name'];
        this.required = data['required'];
        this.expired = data['expired'];
        this.examinationDateComment =
          i18n('language_proficiency.examination_date_comment', {
            month: this.expired,
          });
        this.upload = data['upload'];
        this.scoreInput = data['score_input'];
        this.scoreRequired = data['score_required'];
        this.attachment = new Attachment(this);
      }
      onchangechecked(event: Event) {
        const checkbox = event.target as HTMLInputElement;
        if (checkbox.checked && this.question.max &&
            this.question.max <= this.question.entries.filter(
              entry => entry.checked).length) {
          checkbox.checked = false;
          return;
        }
        this.checked = checkbox.checked;
        const template = this.question.page.template;
        const container = template.reference(this, 'input')!;
        const form =
          template.reference(this.question.page, 'form') as HTMLFormElement;
        for (const control of Form.getControls(form)) {
          if (!container.contains(control))
            continue;
          if ('disabled' in control)
            control.disabled = !this.checked;
        }
        this.question.update(this);
      }
      onchangeexaminationdate(event: Event) {
        const input = event.target as HTMLInputElement;
        this.examinationDate = input.value;
      }
      private get index() {
        return this.question.entries.indexOf(this);
      }
      updateDisabled(control: form.Control) {
        if ('disabled' in control)
          control.disabled = !this.checked || !this.question.scoreEnabled();
      }
      checkValidity() {
        if (!this.checked)
          return true;
        const template = this.question.page.template;
        const input = template.reference(this, 'date') as HTMLInputElement;
        const date = Date.parse(this.examinationDate);
        if (!date ||
            !new Date(date).toISOString().startsWith(this.examinationDate)) {
          input.classList.add('invalid-input');
          input.setCustomValidity(i18n('invalid'));
          return false;
        }
        if (date < Date.parse(`${this.expired}-01`)) {
          input.classList.add('invalid-input');
          input.setCustomValidity(i18n('language_proficiency.examination_date', {expired: this.expired}));
          return false;
        }
        return true;
      }
      onsubmit() {
        const template = this.question.page.template;
        const container =
          template.reference(this, 'entry') as HTMLDivElement;
        const form =
          template.reference(this.question.page, 'form') as HTMLFormElement;
        for (const control of Form.getControls(form)) {
          if (!container.contains(control))
            continue;
          const regexp =
            /\[(language_proficiencies|attachments)_attributes\]\[\d+\](.+)$/;
          control.name =
            control.name.replace(regexp, `[$1_attributes][${this.index}]$2`);
        }
      }
      onformdata(data: FormData) {
        if (this.checked && this.question.scoreEnabled())
          this.attachment.onformdata(data, this.index);
      }
    }

    class Attachment {
      id = 0;
      has = false;
      href = '';
      name = '';
      filename = '';
      private file?: File;
      constructor(readonly entry: Entry) {
      }
      async onchangefile(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files![0]!;
        input.value = '';
        if (!await this.validate(file, input))
          return;
        this.name = input.name;
        this.has = true;
        this.href = URL.createObjectURL(file);
        this.filename = file.name;
        this.file = file;
        this.entry.question.update(this);
      };
      private validate(file: File, input: HTMLInputElement) {
        if (!this.validateFileSize(file))
          return this.error(i18n('file_size_error', {size: '25MB'}));
        if (!this.validateFileType(file, input))
          return this.error(i18n('file_type_error'));
        return true;
      }
      private validateFileSize(file: File) {
        return 0 < file.size && file.size <= 25 * 1024 * 1024;
      }
      private validateFileType(file: File, input: HTMLInputElement) {
        const accept = input.accept;
        if (!accept)
          return true;
        const types = accept.split(',');
        return types.some(type => this.accept(file, type));
      }
      private accept(file: File, type: string) {
        if (type.startsWith('.'))
          return file.name.endsWith(type);
        if (/\/\*$/.test(type))
          return file.type.replace(/\/.+$/, '/*') == type;
        return file.type == type;
      }
      private async error(message: string) {
        await dialog.alert(message);
        return false;
      }
      onclickview() {
        window.open(this.href);
      }
      onclickdelete() {
        this.has = false;
        this.file = undefined;
        this.entry.question.update(this);
      };
      updateDisabled(control: form.Control) {
        this.entry.updateDisabled(control);
      }
      onformdata(data: FormData, index: number) {
        if (!this.file)
          return;
        const name = this.name.replace(/\[\d+\]\[file\]/, `[${index}][file]`);
        data.append(name, this.file, this.file.name);
      }
      
    }
  }

  export class CreditCard extends Question {
    readonly uploader;
    readonly detail;
    constructor(page: WizardPage, data: any) {
      super(page, data);
      this.detail = data['detail'] ?? '';
      this.uploader = new AttachedManyUploader(page, data['uploads']);
    }
    callbackDetail(element: HTMLElement) {
      if (element.hasChildNodes())
        return;
      const range = element.ownerDocument.createRange();
      range.selectNodeContents(element);
      range.insertNode(range.createContextualFragment(this.detail));
    }
    override onformdata(data: FormData) {
      this.uploader.onformdata(data);
    }
  }

  export class BankTransfer extends Question {
    readonly uploader;
    readonly detail;
    constructor(page: WizardPage, data: any) {
      super(page, data);
      this.detail = data['detail'] ?? '';
      this.uploader = new AttachedManyUploader(page, data['uploads']);
    }
    callbackDetail(element: HTMLElement) {
      if (element.hasChildNodes())
        return;
      const range = element.ownerDocument.createRange();
      range.selectNodeContents(element);
      range.insertNode(range.createContextualFragment(this.detail));
    }
    override onformdata(data: FormData) {
      this.uploader.onformdata(data);
    }
  }

  export class ExaminationFee extends Question {
    readonly required: boolean;
    feeType?: 'settlement_service' | 'bank_transfer';
    readonly useSettlementService: boolean;
    settlementService;
    receptionNumber = '';
    readonly useBankTransfer: boolean;
    bankTransfer;
    readonly useBoth;
    constructor(page: WizardPage, data: any) {
      super(page, data);
      this.required = data['required'];
      this.useSettlementService = data['use_settlement_service'];
      this.settlementService = new ExaminationFee.Uploader(this,
        data['settlement_service'] ? [data['settlement_service']] : []);
      this.receptionNumber = data['reception_number'] ?? '';
      this.useBankTransfer = data['use_bank_transfer'];
      this.bankTransfer = new ExaminationFee.Uploader(
        this, data['bank_transfer'] ? [data['bank_transfer']] : []);
      this.useBoth = this.useSettlementService && this.useBankTransfer;
      if (this.useBoth)
        this.feeType = data['fee_type'];
      else if (this.useSettlementService)
        this.feeType = 'settlement_service';
      else if (this.useBankTransfer)
        this.feeType = 'bank_transfer';
    }
    onchangesettlementservice() {
      this.feeType = 'settlement_service';
      this.settlementService = new ExaminationFee.Uploader(this);
      this.bankTransfer = new ExaminationFee.Uploader(this);
      this.receptionNumber = '';
      this.page.template.update(this);
    }
    settlementServiceCallback(element: HTMLElement) {
      if (this.feeType == 'settlement_service')
        element.classList.remove('standby');
      else
        element.classList.add('standby');
    }
    onchangereceptionnumber(event: Event) {
      const input = event.target as HTMLInputElement;
      this.receptionNumber = input.value;
    }
    onchangebanktransfer() {
      this.feeType = 'bank_transfer';
      this.settlementService = new ExaminationFee.Uploader(this);
      this.bankTransfer = new ExaminationFee.Uploader(this);
      this.receptionNumber = '';
      this.page.template.update(this);
    }
    bankTransferCallback(element: HTMLElement) {
      if (this.feeType == 'bank_transfer')
        element.classList.remove('standby');
      else
        element.classList.add('standby');
    }
    override onformdata(data: FormData) {
      this.settlementService.onformdata(data);
      this.bankTransfer.onformdata(data);
    }
  }

  export namespace ExaminationFee {
    export class Uploader extends AttachedOneUploader {
      constructor(readonly question: ExaminationFee, data?: any) {
        super(question.page, data);
      }
      protected override createClientAttachment(name: string, file: File) {
        return super.createClientAttachment(name, file);
      }
      override onsubmit() {
      }
    }
  }

  export class NotRequiredFee extends Question {
    readonly onchange: EventListener = event => {
      const input = event.target as HTMLInputElement;
      this.selected.splice(0);
      this.selected.push(input.value);
      this.page.template.update();
    };
    checked(value: string) {
      return this.selected.includes(value);
    }
  }
}

class WizardPage {
  static page: WizardPage;
  readonly questions: Question[];
  readonly isProxyLogin: boolean;
  readonly countries = countries;
  readonly template: Template;
  constructor() {
    WizardPage.page = this;
    const element = document.querySelector('#wizard') as HTMLElement;
    const root = element.parentElement!;
    element.remove();
    const data = JSON.parse(element.dataset['json']!);
    this.questions = data.questions.map((data: any) =>  {
      const constructors: {
        [type: string]: typeof Question;
      } = {
        radio: Question.Radio,
        checkbox: Question.Checkbox,
        select: Question.Select,
        file: Question.FileQuestion,
        aao: Question.Aao,
        personal_information: Question.PersonalInformation,
        contact_information: Question.ContactInformation,
        graduate_education: Question.GraduateEducation,
        undergraduate_education: Question.UndergraduateEducation,
        higher_education: Question.HigherEducation,
        secondary_education: Question.SecondaryEducation,
        elementary_education: Question.ElementaryEducation,
        other_education: Question.OtherEducation,
        employment_record: Question.EmploymentRecord,
        internship: Question.Internship,
        language_proficiency: Question.LanguageProficiency,
        credit_card: Question.CreditCard,
        bank_transfer: Question.BankTransfer,
        examination_fee: Question.ExaminationFee,
        not_required_fee: Question.NotRequiredFee,
      };
      const constructor = constructors[data['type']] ?? Question;
      return new constructor(this, data);
    });
    this.isProxyLogin = data['proxy_login'];
    this.template = new Template(element, root, this);
  }
  question(id: number) {
    return this.questions.find(question => question.id == id)!;
  }
  async checkValidity() {
    const valids = await Promise.all(this.questions.filter(
      question => question.created()
    ).map(
      question => question.checkValidity()));
    return valids.every(valid => valid);
  }
  onsubmit() {
    for (const question of this.questions)
      question.onsubmit();
  }
  onformdata(event: FormDataEvent) {
    for (const question of this.questions)
      question.onformdata(event.formData);
  }
}

window.addEventListener('DOMContentLoaded', () => {
  if (location.pathname.split('/')[2] == 'wizards') {
    WizardPage.page = new WizardPage;
  }
});
